<script setup lang="ts">
import { computed } from 'vue';

import {
  ConnectionStateService,
  ConnectionStatus,
} from '../services/ConnectionStateService';

const props = defineProps<{
  connectionState: ConnectionStateService;
}>();

// Show blocking overlay for disconnect/reconnecting, but not for server paused
const isBlockingVisible = computed(() => {
  const status = props.connectionState.state.status;
  return status === ConnectionStatus.DISCONNECTED || status === ConnectionStatus.RECONNECTING;
});

// Show subtle paused indicator when server is paused
const isPausedVisible = computed(
  () => props.connectionState.state.status === ConnectionStatus.SERVER_PAUSED,
);

const statusText = computed(() => {
  switch (props.connectionState.state.status) {
    case ConnectionStatus.RECONNECTING:
      return 'Reconnecting...';
    case ConnectionStatus.DISCONNECTED:
      return 'Disconnected';
    default:
      return '';
  }
});
</script>

<template>
  <!-- Blocking overlay for disconnected states -->
  <div
    v-if="isBlockingVisible"
    class="connection-overlay"
  >
    <div class="overlay-content">
      <div class="spinner" />
      <p>{{ statusText }}</p>
    </div>
  </div>

  <!-- Subtle paused indicator (doesn't block clicks) -->
  <div
    v-if="isPausedVisible"
    class="paused-indicator"
  >
    Server Paused
  </div>
</template>

<style scoped>
.connection-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
}

.overlay-content {
  text-align: center;
  color: white;
  font-family:
    'Inter',
    system-ui,
    -apple-system,
    sans-serif;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.overlay-content p {
  font-size: 18px;
  margin: 0;
}

.paused-indicator {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(255, 152, 0, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-family:
    'Inter',
    system-ui,
    -apple-system,
    sans-serif;
  font-size: 12px;
  font-weight: 600;
  z-index: 999998;
  pointer-events: none;
}
</style>
